import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users, User, Check,
  Search, Inbox, FileText, X, RefreshCw,
} from 'lucide-react';
import moment from 'moment';
import { useAuth } from '../../../context/AuthContext';
import { residencesService } from '../../../services';
import { PageHeader } from '../../../components';
import { resolveUploadUrl } from '../../../components/UserPhoto';
import Skeleton from '../../../skeletons/Skeleton';

/**
 * Care Notes — every care note in the organisation, in one feed.
 *
 * The dashboard twin of the mobile app's care-notes / handovers screen
 * (care_notes_log_page.dart): a week strip to pick the day, a Handover toggle,
 * and a resident picker whose first row is "All residents". All filtering is
 * done by the API (GET /residences/:id|all/care-notes), so this list and the
 * phone's can never disagree about what was recorded.
 *
 * Every filter lives in the URL (?resident=&day=&view=&category=) so a view can
 * be linked to, and the back button undoes a filter change. Handovers is the
 * same page on its own route (/manager/handovers), not a query param, because
 * it has its own sidebar entry under Care Records.
 */

const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

const CHIP_BASE =
  'inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-[13px] font-medium whitespace-nowrap transition-colors';
const CHIP_ON = 'bg-blue-600 border-blue-600 text-white shadow-[0_4px_12px_-4px_rgba(37,99,235,0.6)]';
const CHIP_OFF =
  'bg-white dark:bg-[#262b33] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-500';

const DAY_FORMAT = 'YYYY-MM-DD';

const PRIORITY_STYLE = {
  high: { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  low: { dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  routine: { dot: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-400' },
};
const priorityStyle = (p) =>
  PRIORITY_STYLE[String(p || '').toLowerCase()] || { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' };

// The API sends 'N/A' for a resident with no room recorded.
const roomLabel = (room) => (!room || room === 'N/A' ? '' : room);

const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// ─── Resident picker ─────────────────────────────────────────────────────────
// A dropdown with search; "All residents" is pinned above the results so it is
// always one click away, as on the phone.
const ResidentPicker = ({ residents, loading, selectedId, onSelect, noun, nounPlural }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = residents.find((r) => String(r.id) === String(selectedId));
  const query = q.trim().toLowerCase();
  const filtered = query
    ? residents.filter((r) => `${r.name || ''} ${roomLabel(r.room_number)} ${r.unit || ''}`.toLowerCase().includes(query))
    : residents;

  const pick = (id) => { onSelect(id); setOpen(false); setQ(''); };

  const Row = ({ active, onClick, avatar, title, subtitle }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active
        ? 'bg-blue-50 dark:bg-[#31373f]'
        : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
    >
      {avatar}
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-gray-800 dark:text-gray-100 truncate">{title}</span>
        <span className="block text-[11.5px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</span>
      </span>
      {active && <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${CHIP_BASE} max-w-[240px] ${selected ? CHIP_ON : CHIP_OFF}`}
      >
        {selected ? <User className="w-4 h-4 shrink-0" /> : <Users className="w-4 h-4 shrink-0" />}
        <span className="truncate">{selected ? selected.name : `All ${nounPlural.toLowerCase()}`}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-11 z-40 w-[300px] max-w-[calc(100vw-32px)] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-2xl shadow-[0_16px_40px_rgba(15,23,42,0.16)] p-2">
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or room"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-gray-50 dark:bg-[#1f232a] border border-gray-200 dark:border-gray-700 text-[13px] text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:border-blue-400"
            />
          </div>

          <Row
            active={!selected}
            onClick={() => pick(null)}
            avatar={(
              <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-[#1f232a] flex items-center justify-center shrink-0">
                <Users className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" />
              </span>
            )}
            title={`All ${nounPlural.toLowerCase()}`}
            subtitle="Notes from everyone"
          />
          <div className="border-t border-gray-100 dark:border-gray-800 my-1.5" />

          <div className="max-h-[320px] overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-1">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-11 w-full rounded-xl" />)}
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-[12.5px] text-gray-500 dark:text-gray-400 py-6">
                {residents.length === 0 ? `No ${nounPlural.toLowerCase()} found` : `No match for "${q.trim()}"`}
              </p>
            ) : (
              filtered.map((r) => {
                const img = resolveUploadUrl(r.image_url);
                const room = [roomLabel(r.room_number), r.unit].filter(Boolean).join(' · ');
                return (
                  <Row
                    key={r.id}
                    active={String(r.id) === String(selectedId)}
                    onClick={() => pick(r.id)}
                    avatar={img ? (
                      <img src={img} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-[12px] font-bold flex items-center justify-center shrink-0">
                        {initials(r.name)}
                      </span>
                    )}
                    title={r.name || `Unnamed ${noun.toLowerCase()}`}
                    subtitle={room || '—'}
                  />
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Week strip ──────────────────────────────────────────────────────────────
const WeekStrip = ({ day, view, onSelectDay, onShiftWeek, onToday, onViewChange }) => {
  const weekStart = moment(day).startOf('isoWeek');
  const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'day'));
  const today = moment().format(DAY_FORMAT);

  return (
    <div className={`${CARD} p-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onShiftWeek(-1)}
            className="w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:border-blue-300"
            title="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-100 min-w-[150px] text-center">
            {weekStart.format('D MMM')} – {weekStart.clone().add(6, 'day').format('D MMM YYYY')}
          </span>
          <button
            type="button"
            onClick={() => onShiftWeek(1)}
            className="w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:border-blue-300"
            title="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={day}
            onChange={(e) => e.target.value && onSelectDay(e.target.value)}
            className="h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1f232a] text-[13px] text-gray-700 dark:text-gray-200 outline-none focus:border-blue-400"
          />
          <button
            type="button"
            onClick={onToday}
            className="h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 text-[13px] font-medium text-blue-600 dark:text-blue-400 hover:border-blue-300"
          >
            Today
          </button>
          <div className="flex h-9 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden text-[13px] font-medium">
            {['day', 'week'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onViewChange(v)}
                className={`px-3 capitalize ${view === v
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {days.map((d) => {
          const key = d.format(DAY_FORMAT);
          // In week view the whole row is the selection, so every day is lit.
          const selected = view === 'week' || key === day;
          const isToday = key === today;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDay(key, 'day')}
              className={`flex flex-col items-center justify-center py-2 rounded-xl transition-colors ${selected
                ? (view === 'week' ? 'bg-blue-50 dark:bg-[#31373f] text-blue-700 dark:text-blue-300' : 'bg-blue-600 text-white')
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
            >
              <span className="text-[11px] font-medium uppercase opacity-80">{d.format('ddd')}</span>
              <span className="text-[16px] font-bold leading-tight">{d.format('D')}</span>
              <span className={`w-1 h-1 rounded-full mt-0.5 ${isToday ? (selected && view === 'day' ? 'bg-white' : 'bg-blue-600') : 'bg-transparent'}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── One note ────────────────────────────────────────────────────────────────
const Entry = ({ entry, showDetails }) => (
  <div>
    <div className="flex items-start gap-2">
      <span className="w-[5px] h-[5px] rounded-full bg-blue-600 mt-[7px] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">{entry.label}</span>
          {showDetails && entry.categoryName && (
            <span className="text-[10.5px] text-gray-500 dark:text-gray-400 whitespace-nowrap">{entry.categoryName}</span>
          )}
        </div>
        {entry.summary && (
          <div className="text-[12px] font-medium text-blue-600 dark:text-blue-400 leading-snug">{entry.summary}</div>
        )}
      </div>
    </div>

    {showDetails && Array.isArray(entry.photos) && entry.photos.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-2 ml-[13px]">
        {entry.photos.map((photo, i) => (
          <a
            key={i}
            href={resolveUploadUrl(photo.url)}
            target="_blank"
            rel="noreferrer"
            title={photo.caption || 'Open full size'}
            className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-400 transition"
          >
            <img src={resolveUploadUrl(photo.url)} alt={photo.caption || `${entry.label} photo ${i + 1}`} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    )}

    {showDetails && Array.isArray(entry.details) && entry.details.length > 0 && (
      <dl className="mt-1.5 ml-[13px] grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {entry.details.map((detail, i) => (
          <React.Fragment key={i}>
            <dt className="text-[11.5px] text-gray-500 dark:text-gray-400">{detail.label}</dt>
            <dd className="text-[11.5px] font-medium text-gray-800 dark:text-gray-200 break-words">{detail.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    )}
  </div>
);

const NoteCard = ({ note, showResident, showDate, onOpenResident }) => {
  const [expanded, setExpanded] = useState(false);
  const entries = Array.isArray(note.entries) ? note.entries : [];
  const visible = expanded ? entries : entries.slice(0, 2);
  const hidden = entries.length - visible.length;
  const hasDetails = entries.some((e) => (e.details && e.details.length) || (e.photos && e.photos.length));
  const canExpand = entries.length > 2 || hasDetails;
  const ps = priorityStyle(note.priority);
  const room = [roomLabel(note.roomNumber), note.unit].filter(Boolean).join(' · ');

  return (
    <div className={`${CARD} p-5`}>
      <div className="flex items-start gap-3">
        <span className="text-[22px] leading-none mt-0.5" title={note.emotion?.label}>
          {note.emotion?.emoji || '📝'}
        </span>
        <div className="flex-1 min-w-0">
          {/* In "All residents" mode lead with WHO the note is about — a
              merged list is unreadable without it. */}
          {showResident ? (
            <button
              type="button"
              onClick={() => onOpenResident(note.residenceId)}
              className="text-[14.5px] font-semibold text-gray-800 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 text-left truncate max-w-full"
              title="Open resident"
            >
              {note.residentName || 'Unknown'}
            </button>
          ) : (
            <span className="text-[14.5px] font-semibold text-gray-800 dark:text-gray-100">{note.staffName}</span>
          )}
          <div className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
            {showResident
              ? [room, `by ${note.staffName}`].filter(Boolean).join('  •  ')
              : (note.emotion?.label && note.emotion.label !== 'Not recorded' ? note.emotion.label : 'Care note')}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[12px] text-gray-500 dark:text-gray-400 tabular-nums">
            {showDate ? moment(note.date).format('ddd D MMM, h:mm A') : moment(note.date).format('h:mm A')}
          </span>
          {note.addToHandover && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-2 py-0.5 rounded-full">
              <Inbox className="w-3 h-3" /> Handover
            </span>
          )}
        </div>
      </div>

      {note.hasDescription && (
        <p className="mt-3 text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
          {note.description}
        </p>
      )}

      {entries.length > 0 ? (
        <>
          <div className="mt-3 rounded-xl bg-gray-50 dark:bg-[#1f232a] px-4 py-3 space-y-2.5">
            {visible.map((entry, i) => (
              <div key={i} className={i > 0 ? 'pt-2.5 border-t border-gray-200 dark:border-gray-700' : ''}>
                <Entry entry={entry} showDetails={expanded} />
              </div>
            ))}
          </div>
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="mt-2 inline-flex items-center gap-0.5 text-[12px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              {expanded ? 'Show less' : (hidden > 0 ? `Show all details (+${hidden} more)` : 'Show all details')}
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </>
      ) : Array.isArray(note.types) && note.types.length > 0 && (
        // Notes filed before the per-action forms carry only summary strings.
        <div className="mt-3 flex flex-wrap gap-1.5">
          {note.types.map((t, i) => (
            <span key={i} className="text-[11px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#1f232a] px-2 py-1 rounded-lg">{t}</span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${ps.dot}`} />
        <span className={`text-[12px] font-medium ${ps.text}`}>{note.priority} priority</span>
      </div>
    </div>
  );
};

const FeedSkeleton = () => (
  <div className="space-y-4">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className={`${CARD} p-5 space-y-3`}>
        <div className="flex items-center gap-3">
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="h-4 w-40 rounded-md" />
          <Skeleton className="h-3 w-16 rounded-md ml-auto" />
        </div>
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-3 w-24 rounded-md" />
      </div>
    ))}
  </div>
);

// ─── Page ────────────────────────────────────────────────────────────────────
// `handoverOnly` = the sidebar's Care Records > Handovers entry
// (/manager/handovers): the same feed, scoped to notes shared to handover.
const CareNotes = ({ handoverOnly = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';
  const noun = isDomiciliary ? 'Client' : 'Resident';
  const nounPlural = isDomiciliary ? 'Clients' : 'Residents';

  const [searchParams, setSearchParams] = useSearchParams();
  const residentId = searchParams.get('resident') || null;
  const dayParam = searchParams.get('day');
  const day = dayParam && moment(dayParam, DAY_FORMAT, true).isValid() ? dayParam : moment().format(DAY_FORMAT);
  const view = searchParams.get('view') === 'week' ? 'week' : 'day';
  const category = searchParams.get('category') || 'All';

  // Merge changes into the URL; null/undefined/defaults drop the key so a
  // plain /manager/care-notes stays the "today, everyone" view.
  const setFilters = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '' || v === false) next.delete(k);
        else next.set(k, v === true ? '1' : String(v));
      });
      return next;
    });
  }, [setSearchParams]);

  const [residents, setResidents] = useState([]);
  const [residentsLoading, setResidentsLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    residencesService.getAllResidences()
      .then((res) => setResidents(res?.success ? (res.data || []) : []))
      .catch(() => setResidents([]))
      .finally(() => setResidentsLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const unit = view === 'week' ? 'isoWeek' : 'day';
    const from = moment(day, DAY_FORMAT).startOf(unit);
    const to = moment(day, DAY_FORMAT).endOf(unit);
    setLoading(true);
    residencesService.getResidenceCareNotes(residentId || 'all', {
      from: from.toISOString(),
      to: to.toISOString(),
      ...(handoverOnly ? { handover: 'true' } : {}),
      ...(category !== 'All' ? { category } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        setNotes(res?.data || []);
        setCategories(res?.availableCategories || []);
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setNotes([]);
        setError(err?.response?.data?.message || 'Failed to load care notes');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [residentId, day, view, handoverOnly, category, reloadKey]);

  // Free-text search over what's already loaded — staff, resident, the note
  // itself and every recorded answer.
  const visibleNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => {
      const hay = [
        n.residentName, n.staffName, n.hasDescription ? n.description : '',
        ...(n.types || []),
        ...(n.entries || []).flatMap((e) => [e.label, e.summary, ...(e.details || []).map((d) => `${d.label} ${d.value}`)]),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [notes, search]);

  // Week view groups the feed by day, newest first (the API order).
  const groups = useMemo(() => {
    if (view !== 'week') return [{ key: day, notes: visibleNotes }];
    const map = new Map();
    visibleNotes.forEach((n) => {
      const key = moment(n.date).format(DAY_FORMAT);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    });
    return Array.from(map, ([key, list]) => ({ key, notes: list }));
  }, [visibleNotes, view, day]);

  const handoverCount = visibleNotes.filter((n) => n.addToHandover).length;
  const highCount = visibleNotes.filter((n) => String(n.priority).toLowerCase() === 'high').length;
  const selectedResident = residents.find((r) => String(r.id) === String(residentId));
  const allMode = !residentId;

  const periodLabel = view === 'week'
    ? `week of ${moment(day).startOf('isoWeek').format('D MMM')}`
    : (day === moment().format(DAY_FORMAT) ? 'today' : moment(day).format('ddd D MMM YYYY'));

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title={handoverOnly ? 'Handovers' : 'Care Notes'}
        count={loading ? null : visibleNotes.length}
        subtitle={
          allMode
            ? `Everything staff recorded for all ${nounPlural.toLowerCase()}, ${periodLabel}.`
            : `Notes for ${selectedResident?.name || `this ${noun.toLowerCase()}`}, ${periodLabel}.`
        }
        actions={(
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="h-10 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] text-[13px] font-medium text-gray-700 dark:text-gray-200 inline-flex items-center gap-2 hover:border-blue-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        )}
      />

      <WeekStrip
        day={day}
        view={view}
        onSelectDay={(d, v) => setFilters({ day: d === moment().format(DAY_FORMAT) ? null : d, ...(v ? { view: v === 'day' ? null : v } : {}) })}
        onShiftWeek={(delta) => {
          const d = moment(day, DAY_FORMAT).add(delta, 'week').format(DAY_FORMAT);
          setFilters({ day: d === moment().format(DAY_FORMAT) ? null : d });
        }}
        onToday={() => setFilters({ day: null })}
        onViewChange={(v) => setFilters({ view: v === 'day' ? null : v })}
      />

      {/* Filters: Handover toggle, resident picker, then the category chips
          for what is on screen — the same order as the phone. */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            // Handover is its own page (its own sidebar entry), so the chip
            // moves between the two and carries the other filters across.
            onClick={() => navigate({
              pathname: handoverOnly ? '/manager/care-notes' : '/manager/handovers',
              search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            })}
            className={`${CHIP_BASE} ${handoverOnly ? CHIP_ON : CHIP_OFF}`}
          >
            <Inbox className="w-4 h-4" /> Handover
          </button>

          <ResidentPicker
            residents={residents}
            loading={residentsLoading}
            selectedId={residentId}
            onSelect={(id) => setFilters({ resident: id, category: null })}
            noun={noun}
            nounPlural={nounPlural}
          />

          <div className="relative ml-auto w-full sm:w-[260px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search notes, staff, answers…"
              className="w-full h-9 pl-9 pr-8 rounded-full bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-700 text-[13px] text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:border-blue-400"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {(categories.length > 0 || category !== 'All') && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['All', ...categories.filter((c) => c !== 'All'), ...(category !== 'All' && !categories.includes(category) ? [category] : [])].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilters({ category: c === 'All' ? null : c })}
                className={`${CHIP_BASE} h-8 px-3.5 text-[12.5px] ${c === category ? CHIP_ON : CHIP_OFF}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {!loading && !error && visibleNotes.length > 0 && (
        <div className="flex flex-wrap gap-2 text-[12.5px]">
          <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-[#262b33] text-gray-700 dark:text-gray-300">
            {visibleNotes.length} note{visibleNotes.length === 1 ? '' : 's'}
          </span>
          {handoverCount > 0 && !handoverOnly && (
            <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              {handoverCount} for handover
            </span>
          )}
          {highCount > 0 && (
            <span className="px-3 py-1 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              {highCount} high priority
            </span>
          )}
        </div>
      )}

      {loading ? (
        <FeedSkeleton />
      ) : error ? (
        <div className={`${CARD} p-10 text-center`}>
          <p className="text-[13px] text-red-500">{error}</p>
        </div>
      ) : visibleNotes.length === 0 ? (
        <div className={`${CARD} p-12 flex flex-col items-center text-center`}>
          <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-[14px] font-semibold text-gray-700 dark:text-gray-200">
            {search ? `No notes match "${search.trim()}"` : `No ${handoverOnly ? 'handover ' : ''}notes logged for ${view === 'week' ? 'this week' : 'this day'}.`}
          </p>
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-1">
            Try another day{handoverOnly ? ', turn off Handover' : ''}{allMode ? '' : ` or switch to all ${nounPlural.toLowerCase()}`}.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.key} className="space-y-3">
              {view === 'week' && (
                <h3 className="text-[13px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {moment(g.key).format('dddd D MMMM')}
                  <span className="ml-2 normal-case font-medium text-gray-400">· {g.notes.length}</span>
                </h3>
              )}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                {g.notes.map((n) => (
                  <NoteCard
                    key={n.id}
                    note={n}
                    showResident={allMode}
                    showDate={false}
                    onOpenResident={(id) => id && navigate(`/manager/residences/${id}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CareNotes;
